/**
 * Journey detection events — the Tier-2 dispatch helper.
 *
 * Product touchpoints (ChatInput, ModelSelector, VaultShareDrawer, …) call
 * `emitJourneyEvent('model-switched')` inside the single funnel that already
 * performs the action, so an event can never fire for a click that did
 * nothing. This file is deliberately dependency-free: touchpoints import it
 * without pulling any journey UI, and it works whether or not a
 * JourneyProvider is mounted (no provider ⇒ nobody is listening ⇒ no-op).
 *
 * Naming: `cb:<event>` on window. The names below are the single source of
 * truth — journeyConfig references THESE constants, never a bare string, so
 * a typo cannot silently break a detection.
 */

export const JOURNEY_EVENTS = {
  conversationStarted: 'conversation-started',
  messageSent: 'message-sent',
  modelSwitched: 'model-switched',
  fileAttached: 'file-attached',
  libraryVisited: 'library-visited',
  agentEditOpened: 'agent-edit-opened',
  aoaMessageSent: 'aoa-message-sent',
  agentCreated: 'agent-created',
  projectCreated: 'project-created',
  insightsViewed: 'insights-viewed',
  adminOpened: 'admin-opened',
  adminUsersViewed: 'admin-users-viewed',
  adminHierarchyViewed: 'admin-hierarchy-viewed',
  settingsOpened: 'settings-opened',
} as const;

export type JourneyEventName = (typeof JOURNEY_EVENTS)[keyof typeof JOURNEY_EVENTS];

const PREFIX = 'cb:';

/** Fire a journey detection event. Safe to call anywhere, any time. */
export function emitJourneyEvent(
  name: JourneyEventName,
  detail?: Record<string, unknown>,
): void {
  try {
    window.dispatchEvent(new CustomEvent(PREFIX + name, { detail }));
  } catch {
    // A detection must never break the action it observes.
  }
}

/** Subscribe to a journey detection event. Returns the unsubscribe. */
export function onJourneyEvent(
  name: JourneyEventName,
  handler: (detail?: Record<string, unknown>) => void,
): () => void {
  const listener = (e: Event) => handler((e as CustomEvent).detail);
  window.addEventListener(PREFIX + name, listener);
  return () => window.removeEventListener(PREFIX + name, listener);
}
